import React, { useEffect, useState } from 'react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import PageBanner from '../../components/PageBanner';
import { FaCogs, FaDraftingCompass, FaFilePdf, FaLightbulb, FaMicrochip, FaUsers } from 'react-icons/fa';
import { IoArrowForwardOutline } from 'react-icons/io5';
import './idealab.scss';
import assetUrl, { mediaUrl } from '../../services/assetUrl';
import { getIdeaLab } from '../../services/data.service';

// Capability icon keys → icon components.
const CAPABILITY_ICONS = {
  innovation: <FaLightbulb />,
  prototyping: <FaDraftingCompass />,
  teams: <FaUsers />,
  industry: <FaCogs />,
};

const IdeaLab = () => {
  // Backend-driven (DB-only). No bundled fallback — the page renders
  // loading → content → unavailable states from the /idealab singleton.
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getIdeaLab();
        const payload = res?.data?.data || res?.data || null;
        if (active) setData(payload);
      } catch (e) {
        // backend unavailable / not seeded
        if (active) setData(null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const hero = data?.hero || {};
  const intro = data?.intro || {};
  const highlights = data?.highlights || [];
  const capabilities = data?.capabilities || {};
  const capabilityItems = capabilities.items || [];
  const equipment = data?.equipment || {};
  const equipmentItems = equipment.items || [];
  const team = data?.team || {};
  const teamMembers = team.members || [];
  const reference = data?.reference || {};
  const ad = reference.ad || {};

  if (loading || !data) {
    return (
      <div className="idealab-page">
        <Header />
        <PageBanner title="AICTE IDEA Lab" eyebrow="Innovation" />
        <main className="idealab-main container">
          <div className="idealab-state" style={{ padding: '5rem 0', textAlign: 'center' }}>
            {loading ? 'Loading IDEA Lab content…' : 'IDEA Lab content is currently unavailable.'}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="idealab-page">
      <Header />
      <PageBanner
        title={hero.title}
        eyebrow={hero.eyebrow}
        subtitle={hero.subtitle}
        image={hero.bannerImage ? mediaUrl(hero.bannerImage) : undefined}
      />

      <main className="idealab-main container">
        <section className="idealab-intro">
          <article className="idealab-intro__story">
            <span className="idealab-kicker">{intro.kicker}</span>
            <h2>{intro.heading}</h2>
            {(intro.paragraphs || []).map((para, idx) => (
              <p key={idx}>{para}</p>
            ))}
            <div className="idealab-intro__actions">
              <a href={assetUrl("/view/idealab/Tender_Notice.pdf")} target="_blank" rel="noreferrer">
                <FaFilePdf />
                Tender Notice
              </a>
              <a href="mailto:idealab@ncetmail.com">
                <IoArrowForwardOutline />
                idealab@ncetmail.com
              </a>
              {intro.tenderPdf && (
                <a href={mediaUrl(intro.tenderPdf)} target="_blank" rel="noreferrer">
                  <FaFilePdf />
                  {intro.tenderLabel || 'Tender Notice'}
                </a>
              )}
              {intro.email && (
                <a href={`mailto:${intro.email}`}>
                  <IoArrowForwardOutline />
                  {intro.email}
                </a>
              )}
            </div>
          </article>
          <aside className="idealab-intro__stats">
            {highlights.map((item) => (
              <div key={item.label} className="idealab-stat">
                <span>{item.label}</span>
                <strong>{item.value}</strong>
              </div>
            ))}
          </aside>
        </section>

        <section className="idealab-capabilities">
          <header className="idealab-section-head">
            <span className="idealab-kicker">{capabilities.eyebrow}</span>
            <h2>{capabilities.heading}</h2>
          </header>
          <div className="idealab-capabilities__grid">
            {capabilityItems.map((item) => (
              <article key={item.title} className="idealab-capability-card">
                <span className="icon">{CAPABILITY_ICONS[item.icon] || <FaLightbulb />}</span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="idealab-equipment">
          <header className="idealab-section-head">
            <span className="idealab-kicker">{equipment.eyebrow}</span>
            <h2>{equipment.heading}</h2>
          </header>
          <div className="idealab-equipment__panel">
            <div className="idealab-equipment__lead">
              <span className="icon"><FaMicrochip /></span>
              <p>{equipment.lead}</p>
            </div>
            <div className="idealab-equipment__chips">
              {equipmentItems.map((item) => (
                <span key={item}>{item}</span>
              ))}
            </div>
          </div>
        </section>

        <section className="idealab-team">
          <header className="idealab-section-head">
            <span className="idealab-kicker">{team.eyebrow}</span>
            <h2>{team.heading}</h2>
          </header>
          <div className="idealab-team__grid">
            {teamMembers.map((member) => (
              <article key={member.name} className="idealab-team-card">
                <div className="idealab-team-card__image">
                  <img src={mediaUrl(member.image)} alt={member.name} loading="lazy" />
                </div>
                <h3>{member.name}</h3>
                <p className="role">{member.role}</p>
                <p className="dept">{member.dept}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="idealab-reference">
          <article className="idealab-reference__card">
            <span className="idealab-kicker">{reference.kicker}</span>
            <h2>{reference.heading}</h2>
            <p>{reference.text}</p>
            {reference.email && (
              <a href={`mailto:${reference.email}`}>For queries: {reference.email}</a>
            )}
          </article>
          <article className="idealab-reference__ad">
            <h3>Newspaper Advertisement</h3>
            <img src={assetUrl("/view/idealab/announcement.png")} alt="AICTE IDEA Lab newspaper advertisement" loading="lazy" />
            <h3>{ad.heading}</h3>
            {ad.image && (
              <img src={mediaUrl(ad.image)} alt={ad.heading || 'AICTE IDEA Lab newspaper advertisement'} loading="lazy" />
            )}
          </article>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default IdeaLab;
